import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { LS_KEYS } from '../constants';
import { AdditionalQueryParams } from './usePagination';
import usePersist from './usePersist';

type CasinoTableQueryBase = {
  page: number;
  limit: number;
};

type SortDefaults<TQuery> = {
  sortColumn: Extract<keyof TQuery, string>;
  sortOrder: Extract<keyof TQuery, string>;
  defaultColumn: string;
  defaultOrder: 'asc' | 'desc';
};

type DateRangeQuery = {
  dateFrom: number;
  dateTo: number;
};

type UseCasinoTableQueryOptions<TQuery extends CasinoTableQueryBase> = {
  defaultQuery: TQuery | (() => TQuery);
  initialQueryParams?: Partial<TQuery>;
  usePersistedFilters?: boolean;
  emptyFields?: Array<Extract<keyof TQuery, string>>;
  emptyArrayFields?: Array<Extract<keyof TQuery, string>>;
  searchField?: Extract<keyof TQuery, string>;
  sortDefaults?: SortDefaults<TQuery>;
  prepareQuery?: (query: TQuery) => TQuery;
};

const resolveDefaultQuery = <TQuery extends CasinoTableQueryBase>(defaultQuery: TQuery | (() => TQuery)) =>
  typeof defaultQuery === 'function' ? (defaultQuery as () => TQuery)() : defaultQuery;

export const sanitizeCasinoTableQuery = <TQuery extends CasinoTableQueryBase>(
  query: TQuery,
  {
    emptyFields = [],
    emptyArrayFields = [],
    prepareQuery,
  }: Pick<UseCasinoTableQueryOptions<TQuery>, 'emptyFields' | 'emptyArrayFields' | 'prepareQuery'> = {}
): TQuery => {
  const nextQuery = prepareQuery ? prepareQuery({ ...query }) : { ...query };

  emptyFields.forEach((field) => {
    if (!nextQuery[field]) {
      delete nextQuery[field];
    }
  });

  emptyArrayFields.forEach((field) => {
    const value = nextQuery[field];

    if (!Array.isArray(value) || !value.length) {
      delete nextQuery[field];
    }
  });

  return nextQuery;
};

export const useCasinoTableQuery = <TQuery extends CasinoTableQueryBase>({
  defaultQuery,
  initialQueryParams,
  usePersistedFilters = false,
  emptyFields,
  emptyArrayFields,
  searchField,
  sortDefaults,
  prepareQuery,
}: UseCasinoTableQueryOptions<TQuery>) => {
  const { pathname } = useLocation();
  const { getPersistData } = usePersist(false);

  const sanitizeQuery = useCallback(
    (query: TQuery) =>
      sanitizeCasinoTableQuery(query, {
        emptyFields,
        emptyArrayFields,
        prepareQuery,
      }),
    [emptyArrayFields, emptyFields, prepareQuery]
  );

  const persistedFilters = useMemo(
    () => (usePersistedFilters ? (getPersistData<Partial<TQuery>>(`${LS_KEYS.appFormFilter}-${pathname}`) ?? {}) : {}),
    [getPersistData, pathname, usePersistedFilters]
  );

  const [queryParams, setQueryParams] = useState<TQuery>(() =>
    sanitizeQuery({
      ...resolveDefaultQuery(defaultQuery),
      ...persistedFilters,
      ...initialQueryParams,
    } as TQuery)
  );

  const paginationModel = useMemo(
    () => ({
      page: queryParams.page - 1,
      pageSize: queryParams.limit,
    }),
    [queryParams.limit, queryParams.page]
  );

  const updateQueryParams = useCallback((model: GridPaginationModel) => {
    setQueryParams((prevValue) => ({
      ...prevValue,
      page: model.page + 1,
      limit: model.pageSize,
    }));
  }, []);

  const changeQuery = useCallback(
    (query: AdditionalQueryParams) => {
      setQueryParams((prevValue) =>
        sanitizeQuery({
          ...prevValue,
          ...query,
          page: 1,
        } as TQuery)
      );
    },
    [sanitizeQuery]
  );

  const handleSearch = useCallback(
    (value: string | null) => {
      if (!searchField) return;

      setQueryParams((prevValue) =>
        sanitizeQuery({
          ...prevValue,
          [searchField]: value,
          page: 1,
        } as TQuery)
      );
    },
    [sanitizeQuery, searchField]
  );

  const handleSort = useCallback(
    (model: GridSortModel) => {
      if (!sortDefaults) return;

      const sortModelItem = model[0];

      setQueryParams((prevValue) => ({
        ...prevValue,
        page: 1,
        [sortDefaults.sortColumn]: sortModelItem?.field ?? sortDefaults.defaultColumn,
        [sortDefaults.sortOrder]: sortModelItem?.sort ?? sortDefaults.defaultOrder,
      }));
    },
    [sortDefaults]
  );

  const changeDateRange = useCallback(({ dateFrom, dateTo }: DateRangeQuery) => {
    setQueryParams((prevValue) => ({
      ...prevValue,
      page: 1,
      dateFrom,
      dateTo,
    }));
  }, []);

  return {
    queryParams,
    setQueryParams,
    paginationModel,
    updateQueryParams,
    changeQuery,
    handleSearch,
    handleSort,
    changeDateRange,
  };
};
